import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronUp, Trash2, Bot, Mic, ArrowUp, AlertTriangle, Maximize2, Minimize2 } from 'lucide-react';
import { ChatMessage, ChatParticipant } from '../types/chat';
import { ChatMessages } from './chat/ChatMessages';
import { useKnowledgeChat } from '../hooks/useKnowledgeChat';
import { KnowledgeChatMessage, KnowledgeContext, KnowledgeChatService } from '../services/knowledgeChatService';

interface KnowledgeChatProps {
  isOpen: boolean;
  onClose: () => void;
  content: string;
  title: string;
  itemId: string;
  isFullscreen?: boolean;
  onToggleFullscreen?: () => void;
  backgroundChatService?: KnowledgeChatService | null;
  getCachedHistory?: (knowledgeItemId: string) => KnowledgeChatMessage[] | null;
}

// Компонент модального окна подтверждения
interface ConfirmationModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText: string;
  cancelText: string;
  onConfirm: () => void;
  onCancel: () => void;
  isDestructive?: boolean;
}

function ConfirmationModal({ 
  title, 
  message, 
  confirmText, 
  cancelText, 
  onConfirm, 
  onCancel,
  isDestructive = false 
}: ConfirmationModalProps) {
  return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        onClick={onCancel}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center mb-4">
            <AlertTriangle className={`w-6 h-6 mr-3 ${isDestructive ? 'text-red-500' : 'text-yellow-500'}`} />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {title}
            </h3>
          </div>
          
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            {message}
          </p>
          
          <div className="flex space-x-3 justify-end">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white transition-colors"
            >
              {cancelText}
            </button>
            <button
              onClick={onConfirm}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                isDestructive
                  ? 'bg-red-500 hover:bg-red-600 text-white'
                  : 'bg-blue-500 hover:bg-blue-600 text-white'
              }`}
            >
              {confirmText}
            </button>
          </div>
        </motion.div>
      </motion.div>
  );
}

export function KnowledgeChat({ isOpen, onClose, content, title, itemId, isFullscreen = false, onToggleFullscreen, backgroundChatService, getCachedHistory }: KnowledgeChatProps) {
  const [inputValue, setInputValue] = useState('');
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; messageId: string } | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [isNearBottom, setIsNearBottom] = useState(true);
  
  // Состояния для подтверждений удаления
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    messageId?: string;
    type: 'message' | 'chat';
  }>({
    isOpen: false,
    type: 'message'
  });

  // Контекст базы знаний для чата
  const knowledgeContext: KnowledgeContext = {
    title: title,
    itemType: 'document',
    content: content
  };

  // Используем хук для работы с чатом базы знаний
  const {
    messages: knowledgeMessages,
    isTyping,
    isConnected,
    isLoading,
    error,
    sendMessage: sendKnowledgeMessage,
    deleteMessage: deleteKnowledgeMessage,
    clearChatHistory,
    streamingMessages
  } = useKnowledgeChat({
    knowledgeItemId: itemId,
    knowledgeContext: knowledgeContext,
    backgroundChatService: backgroundChatService,
    cachedHistory: getCachedHistory ? getCachedHistory(itemId) : null,
    onMessageSent: () => setIsSending(false)
  });

  const chatContainerRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);
  const messageInputRef = useRef<HTMLTextAreaElement>(null);

  const currentUser: ChatParticipant = {
    id: 'user',
    name: 'Вы',
    role: 'Пользователь',
    isOnline: true
  };

  const assistant: ChatParticipant = {
    id: 'assistant',
    name: 'AI Ассистент',
    role: 'Эксперт по документу',
    isOnline: true
  };

  // Функция для автоматического увеличения высоты текстового поля
  const autoResizeTextarea = () => {
    if (messageInputRef.current) {
      const textarea = messageInputRef.current;
      const minHeight = 50; // Минимальная высота ~1.5 строки
      const maxHeight = 200; // Максимальная высота ~8 строк (25px * 8)
      
      // Сбрасываем высоту для правильного расчета scrollHeight
      textarea.style.height = minHeight + 'px';
      
      // Вычисляем новую высоту на основе содержимого
      const scrollHeight = textarea.scrollHeight;
      const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight);
      
      textarea.style.height = newHeight + 'px';
    }
  };

  // Функция проверки, находится ли пользователь рядом с концом чата
  const checkIfNearBottom = () => {
    if (messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      const threshold = 150; // Порог в пикселях
      const isNear = container.scrollHeight - container.scrollTop - container.clientHeight <= threshold;
      setIsNearBottom(isNear);
      // Показываем кнопку только если пользователь НЕ рядом с концом и есть сообщения
      setShowScrollToBottom(!isNear && knowledgeMessages.length > 0);
    }
  };

  // Функция прокрутки вниз
  const scrollToBottom = (smooth = true) => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: smooth ? 'smooth' : 'auto'
      });
    }
  };

  useEffect(() => {
    // Автоматически изменяем размер текстового поля при изменении содержимого
    autoResizeTextarea();
  }, [inputValue]);

  // Отслеживание скролла
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkIfNearBottom);
      return () => container.removeEventListener('scroll', checkIfNearBottom);
    }
  }, []);

  // Автопрокрутка при новых сообщениях (только для пользовательских сообщений)
  useEffect(() => {
    if (knowledgeMessages.length > 0) {
      const lastMessage = knowledgeMessages[knowledgeMessages.length - 1];
      // Прокручиваем только если последнее сообщение от пользователя и пользователь был рядом с концом
      if (!lastMessage.isFromAI && isNearBottom) {
        // Небольшая задержка для завершения рендеринга
        setTimeout(() => scrollToBottom(true), 100);
      }
    }
    // Обновляем состояние кнопки прокрутки с задержкой для завершения рендеринга
    setTimeout(() => checkIfNearBottom(), 50);
  }, [knowledgeMessages]);

  useEffect(() => {
    if (isOpen) {
      // Фокус на поле ввода при открытии
      setTimeout(() => {
        if (messageInputRef.current) {
          // Принудительно устанавливаем минимальную высоту
          messageInputRef.current.style.height = '50px';
          messageInputRef.current.focus();
          autoResizeTextarea();
        }
        // Прокручиваем в конец при открытии
        scrollToBottom(false);
        setIsNearBottom(true);
      }, 300);
    }
  }, [isOpen]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target as Node)) {
        setContextMenu(null);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Обработка клавиши Escape для выхода из полноэкранного режима
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && isFullscreen && onToggleFullscreen) {
        onToggleFullscreen();
      }
    }

    if (isFullscreen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isFullscreen, onToggleFullscreen]);

  // Преобразование сообщений базы знаний в формат ChatMessage для совместимости
  const convertKnowledgeMessagesToChat = (): ChatMessage[] => {
    if (!knowledgeMessages) return [];
    
    const validMessages = knowledgeMessages.filter(msg => msg.id && msg.content);

    const convertedMessages = validMessages.map((msg): ChatMessage => {
      return {
        id: msg.id,
        content: msg.content,
        sender: msg.isFromAI ? assistant : currentUser,
        timestamp: new Date(msg.timestamp)
      };
    });
    
    return convertedMessages;
  };

  const handleSendMessage = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    
    if (!inputValue.trim() || !isConnected || isSending) return;

    const message = inputValue.trim();
    setInputValue('');
    setIsSending(true);

    // Возвращаем высоту текстового поля к минимальной
    if (messageInputRef.current) {
      messageInputRef.current.style.height = '50px';
    }

    // Отправляем сообщение через хук
    try {
      await sendKnowledgeMessage(message);
      
      // Прокрутка будет обработана автоматически в useEffect для пользовательских сообщений
      
      // Установка фокуса обратно в поле ввода
      if (messageInputRef.current) {
        messageInputRef.current.focus();
      }
    } catch (error) {
      console.error('Ошибка отправки сообщения:', error);
      // При ошибке возвращаем текст в поле ввода
      setInputValue(message);
    } finally {
      setIsSending(false);
    }
  };

  // Обработчики подтверждения удаления
  const handleDeleteMessageConfirm = (messageId: string) => {
    setDeleteConfirmation({
      isOpen: true,
      messageId: messageId,
      type: 'message'
    });
  };

  const handleClearChatConfirm = () => {
    setDeleteConfirmation({
      isOpen: true,
      type: 'chat'
    });
  };

  const handleConfirmDelete = async () => {
    // Сохраняем информацию для удаления
    const type = deleteConfirmation.type;
    const messageId = deleteConfirmation.messageId;
    
    // Сначала закрываем модальное окно
    setDeleteConfirmation({ isOpen: false, type: 'message' });
    setContextMenu(null);
    
    // Затем выполняем асинхронное удаление
    try {
      if (type === 'message' && messageId) {
        await deleteKnowledgeMessage(messageId);
      } else if (type === 'chat') {
        await clearChatHistory();
      }
    } catch (error) {
      console.error('Ошибка удаления:', error);
      // Ошибки обрабатываются в хуке useKnowledgeChat, 
      // там же происходит восстановление сообщения в случае неудачи
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmation({ isOpen: false, type: 'message' });
  };

  const handleVoiceRecording = () => {
    setIsRecording(!isRecording);
    // TODO: Реализовать функционал записи голоса
  };

  // Прокрутка к новым сообщениям управляется ChatMessages компонентом

  const getConnectionStatus = () => {
    if (!isConnected) return 'Подключение...';
    return 'Онлайн';
  };

  const getConnectionStatusColor = () => {
    if (!isConnected) return 'text-yellow-500';
    if (isTyping) return 'text-blue-500';
    return 'text-green-500';
  };

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: isFullscreen ? 0 : -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: isFullscreen ? 0 : -20 }}
            className={`bg-white dark:bg-gray-800 overflow-hidden shadow-xl border-b border-gray-200/80 dark:border-gray-700/80 ${
              isFullscreen 
                ? 'fixed inset-0 z-50 h-screen flex flex-col pb-20 sm:pb-0' 
                : 'mb-6 w-full'
            }`}
            ref={chatContainerRef}
          >
            {/* Заголовок */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex items-center justify-between">
              <div className="flex items-center space-x-3 min-w-0 flex-1">
                <div className="flex items-center min-w-0 flex-1">
                  <Bot className="w-5 h-5 text-indigo-600 dark:text-indigo-400 mr-2 flex-shrink-0" />
                  <h3 className="text-sm sm:text-lg font-semibold text-gray-900 dark:text-white min-w-0 flex-1">
                    <span className="mr-1">Обсуждение:</span>
                    <span className="text-indigo-600 dark:text-indigo-400 break-words">{title}</span>
                    {isFullscreen && (
                      <span className="text-xs text-gray-400 dark:text-gray-500 ml-2 hidden sm:inline">
                        (ESC - выйти из полноэкранного режима)
                      </span>
                    )}
                  </h3>
                </div>
                <div className="flex items-center space-x-2 flex-shrink-0">
                  <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-yellow-500'}`} />
                  <span className={`text-xs ${getConnectionStatusColor()} hidden sm:inline`}>
                    {getConnectionStatus()}
                  </span>
                </div>
              </div>
              <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
                <button
                  onClick={handleClearChatConfirm}
                  disabled={!isConnected}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-600 dark:text-gray-300 disabled:opacity-50 rounded-lg"
                  title="Очистить чат"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
                {onToggleFullscreen && (
                  <button
                    onClick={onToggleFullscreen}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 text-gray-600 dark:text-gray-300 rounded-lg hover:scale-105"
                    title={isFullscreen ? "Выйти из полноэкранного режима" : "Открыть на весь экран"}
                  >
                    <motion.div
                      initial={false}
                      animate={{ scale: isFullscreen ? 1.1 : 1 }}
                      transition={{ duration: 0.2 }}
                    >
                      {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
                    </motion.div>
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-600 dark:text-gray-300 rounded-lg"
                  title="Скрыть чат"
                >
                  <ChevronUp className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Сообщения */}
            <div 
              ref={messagesContainerRef}
              className={`relative overflow-y-auto bg-white dark:bg-gray-800 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent ${
                isFullscreen ? 'flex-1 min-h-0 px-1 sm:px-2' : 'h-[400px] sm:h-[500px] px-2'
              }`}
            >
              <div className={`mx-auto pt-3 pb-1 ${isFullscreen ? 'max-w-full px-2 sm:px-4' : 'max-w-6xl'}`}>
                <div className={`w-full ${isFullscreen ? 'min-w-full' : 'min-w-[300px] sm:min-w-[750px]'}`}>
                  {error && (
                    <div className="p-4 mb-4 bg-red-100 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
                      <p className="text-red-700 dark:text-red-300">{error}</p>
                    </div>
                  )}
                  {isLoading ? (
                    <div className="flex items-center justify-center p-8">
                      <div className="flex flex-col items-center">
                        <div className="w-8 h-8 border-2 border-indigo-200 dark:border-indigo-700 border-t-indigo-500 dark:border-t-indigo-400 rounded-full animate-spin mb-3"></div>
                        <div className="text-gray-500 dark:text-gray-400">
                          {backgroundChatService ? 'Подготовка чата...' : 'Подключение к чату и загрузка истории...'}
                        </div>
                      </div>
                    </div>
                  ) : knowledgeMessages && knowledgeMessages.length === 0 ? (
                    <div className="flex items-center justify-center p-8">
                      <div className="text-center">
                        <Bot className="w-12 h-12 text-indigo-400 dark:text-indigo-500 mx-auto mb-3" />
                        <p className="text-gray-500 dark:text-gray-400 mb-2">
                          Привет! Я готов обсудить с вами документ "{title}".
                        </p>
                        <p className="text-sm text-gray-400 dark:text-gray-500">
                          Задайте любой вопрос о содержимом документа.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <ChatMessages
                      messages={convertKnowledgeMessagesToChat()}
                      currentUserId={currentUser.id}
                      onDeleteMessage={handleDeleteMessageConfirm}
                      streamingMessageIds={new Set(streamingMessages.keys())}
                    />
                  )}
                  {isTyping && (
                    <div className="p-4 ml-2 mt-2">
                      {/* Анимация загрузки пока ИИ думает */}
                      <div className="flex items-center">
                        <div className="bg-gray-100 dark:bg-gray-700 rounded-lg px-4 py-2">
                          <div className="flex items-center space-x-2">
                            <div className="flex space-x-1">
                              <div className="w-2 h-2 bg-indigo-500 dark:bg-indigo-400 rounded-full typing-indicator-dot"></div>
                              <div className="w-2 h-2 bg-indigo-500 dark:bg-indigo-400 rounded-full typing-indicator-dot"></div>
                              <div className="w-2 h-2 bg-indigo-500 dark:bg-indigo-400 rounded-full typing-indicator-dot"></div>
                            </div>
                            <span className="text-xs text-gray-500 dark:text-gray-400">Думаю...</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>


            </div>

            {/* Ввод сообщения */}
            <div className="relative">
              {/* Кнопка прокрутки вниз по центру */}
              <AnimatePresence>
                {showScrollToBottom && (
                  <motion.button
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    onClick={() => scrollToBottom(true)}
                    className="absolute -top-12 left-1/2 transform -translate-x-1/2 w-8 h-8 bg-white border border-gray-300 dark:border-gray-600 dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full shadow-md flex items-center justify-center transition-all z-10"
                    title="Прокрутить к последнему сообщению"
                  >
                    <ArrowUp className="w-4 h-4 transform rotate-180" />
                  </motion.button>
                )}
              </AnimatePresence>
              
              <form 
                onSubmit={handleSendMessage}
                className="px-4 py-2 bg-white dark:bg-gray-800"
              >
              <div className={`mx-auto ${isFullscreen ? 'max-w-full px-2 sm:px-4' : 'max-w-6xl'}`}>
                <div className="relative flex justify-center">
                  <div className={`rounded-3xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 flex flex-col overflow-hidden w-full ${
                    isFullscreen ? 'max-w-[98%] sm:max-w-[80%]' : 'max-w-[95%] sm:max-w-[80%]'
                  }`}>
                    {/* Текстовое поле */}
                    <textarea
                      ref={messageInputRef}
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                      placeholder={
                        !isConnected ? "Подключение к чату..." :
                        isSending ? "Отправка сообщения..." :
                        "Спросите что-нибудь о документе..."
                      }
                      disabled={!isConnected || isTyping || isSending}
                      className="w-full pt-2 pb-2 px-5 bg-transparent text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 resize-none focus:outline-none overflow-y-auto text-base disabled:opacity-50"
                      style={{ height: '50px', minHeight: '50px', maxHeight: '200px' }}
                    />
                    
                    {/* Нижняя панель с кнопками */}
                    <div className="flex items-end justify-between px-3 py-2">
                      {/* Информация о документе */}
                      <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                      </div>
                      
                      {/* Кнопки справа */}
                      <div className="flex items-center space-x-2 flex-shrink-0">
                        {inputValue.trim() ? (
                          <button
                            type="submit"
                            disabled={!isConnected || isTyping || isSending}
                            className="w-10 h-10 flex items-center justify-center rounded-full transition-all bg-indigo-500 dark:bg-indigo-600 text-white hover:bg-indigo-600 dark:hover:bg-indigo-700 disabled:opacity-50"
                          >
                            {isSending ? (
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <ArrowUp className="w-5 h-5" />
                            )}
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={handleVoiceRecording}
                            disabled={!isConnected || isTyping}
                            className={`w-10 h-10 flex items-center justify-center rounded-full transition-all disabled:opacity-50 ${
                              isRecording 
                                ? 'bg-red-500 text-white animate-pulse shadow-md shadow-red-300 dark:shadow-red-900/30 ring-4 ring-red-300/50 dark:ring-red-500/30' 
                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-200'
                            }`}
                          >
                            <Mic className={`w-5 h-5 ${isRecording ? 'animate-pulse' : ''}`} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              </form>
            </div>

            {/* Контекстное меню */}
            <AnimatePresence>
              {contextMenu && (
                <motion.div
                  ref={contextMenuRef}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="absolute bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700 rounded-lg py-1 z-50"
                  style={{ 
                    left: contextMenu.x, 
                    top: contextMenu.y,
                    transform: 'translateX(-50%)'
                  }}
                >
                  <button
                    onClick={() => handleDeleteMessageConfirm(contextMenu.messageId)}
                    className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center transition-colors"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Удалить сообщение
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Модальное окно подтверждения удаления */}
      <AnimatePresence>
        {deleteConfirmation.isOpen && (
          <ConfirmationModal
            isOpen={deleteConfirmation.isOpen}
            title={deleteConfirmation.type === 'message' ? 'Удалить сообщение?' : 'Очистить весь чат?'}
            message={
              deleteConfirmation.type === 'message' 
                ? 'Это сообщение будет удалено безвозвратно. Продолжить?' 
                : 'Вся история чата будет удалена безвозвратно. Это действие нельзя отменить.'
            }
            confirmText={deleteConfirmation.type === 'message' ? 'Удалить' : 'Очистить чат'}
            cancelText="Отмена"
            onConfirm={handleConfirmDelete}
            onCancel={handleCancelDelete}
            isDestructive={true}
          />
        )}
      </AnimatePresence>
    </>
  );
}
